import styled, { useTheme } from 'styled-components';
import Modal, { FullHeightModalContent } from '../../../components/Modal';
import { CaptionSmall, ParagraphLargeText } from '../../../components/Texts';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import { useEffect, useRef, useState } from 'react';
import Button from '../../../components/Button';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { useCheckFeeMutation } from '../../../redux/api/authorized/zyptoPrepaidCards';
import ContentLoader from 'react-content-loader';
import { formatUSDEnsure2Digits } from '../../../common/currency';

const StyledInput = styled.input`
  width: 100%;
  height: 48px;
  padding-left: 15px;
  padding-right: 15px;
  border-radius: 16px;
  border: 0px solid transparent;
  text-align: center;
  font-size: 64px;
  font-weight: 700;
  background-color: ${({ theme }) => theme.background.primary};
  color: ${({ theme }) => theme.text.title};
  &:focus {
    outline: none;
  }
`;

const AmountButton = styled.button`
  flex: 1;
  border-radius: 16px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? `${props.theme.primary.primary}`
        : `${props.theme.border.primary}`};
  padding: 12px 12px;
  background-color: ${(props) =>
    props.$active
      ? `${props.theme.background.primary}`
      : `${props.theme.background.light}`};
  color: ${({ theme }) => theme.text.title};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$active
          ? `${props.theme.primary.primary}`
          : `${props.theme.text.disabled}`};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AmountButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const AmountSelector = ({
  onClose,
  text = 'Deposit',
  onContinue = () => {},
  minAmount = 0,
  maxAmount = 10000,
  values = [30, 40, 50, 100],
  defaultAmount = 10,
  requiresFee = false,
}) => {
  const [fullyRendered, setFullyRendered] = useState(false);
  const [amount, setAmount] = useState(defaultAmount);
  const inputRef = useRef(null);
  const theme = useTheme();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fullyRendered) return;
    if (!inputRef.current) return;
    inputRef.current.focus();
  }, [fullyRendered, inputRef.current]);

  useEffect(() => {
    if (amount < minAmount) {
      setError('Amount must be greater than or equal to $' + minAmount);
    } else if (amount > maxAmount) {
      setError('Amount must be less than or equal to $' + maxAmount);
    } else {
      setError(null);
    }
  }, [amount, minAmount, maxAmount]);

  const [totalWithFee, setTotalWithFee] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkFee] = useCheckFeeMutation();

  useEffect(() => {
    if (!requiresFee) return;
    let cancelled = false;
    setLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const feeResp = await checkFee({ amount }).unwrap();
        if (!cancelled) {
          if (feeResp?.feeData?.data?.prepaid?.total_to_pay_crypto) {
            setTotalWithFee(
              feeResp?.feeData?.data?.prepaid?.total_to_pay_crypto,
            );
            setLoading(false);
          } else {
          }
        }
      } finally {
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [amount, requiresFee]);

  return (
    <Modal onClose={onClose} onDidPresent={() => setFullyRendered(true)}>
      <FullHeightModalContent>
        <ParagraphLargeText
          style={{ textAlign: 'center', color: theme.text.title }}
        >
          {text}
        </ParagraphLargeText>
        <StyledInput
          autoFocus={true}
          ref={inputRef}
          value={`$${amount}`}
          id='amount-input'
          inputMode='numeric'
          onChange={(e) => setAmount(e.target.value.replace('$', ''))}
        />
        <AmountButtonContainer>
          {values.map((value) => (
            <AmountButton onClick={() => setAmount(value)}>
              ${value}
            </AmountButton>
          ))}
        </AmountButtonContainer>
        {requiresFee && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CaptionSmall>Total:</CaptionSmall>
            {loading ? (
              <ContentLoader
                speed={5}
                width={50}
                height={22}
                viewBox={`0 0 50 22`}
                backgroundColor={theme.background.secondary}
                foregroundColor={theme.special.skeleton}
              >
                <rect x='0' y='0' rx='4' ry='4' width='50' height='22' />
              </ContentLoader>
            ) : (
              formatUSDEnsure2Digits(totalWithFee)
            )}
          </div>
        )}
        <Button
          fullWidth
          disabled={!!error}
          loading={loading}
          onClick={async () => {
            if (
              Capacitor.isNativePlatform() &&
              Capacitor.isPluginAvailable('Keyboard')
            ) {
              setLoading(true);
              await Keyboard.hide();
              await new Promise((resolve) => setTimeout(resolve, 100));
              setLoading(false);
            }
            onContinue(amount, totalWithFee);
          }}
        >
          Continue
        </Button>
      </FullHeightModalContent>
    </Modal>
  );
};

export default AmountSelector;
